import type { Strings } from "./i18n";
import type { Specimen } from "./types";

/**
 * Facts the app already holds and that change how a plant is cared for, not
 * decoration: what the owner eats is never sprayed, a stressed plant is never
 * fed, compacted soil dries slowly, and an unknown last watering is the honest
 * day-one state.
 *
 * The tone is the fact's consequence, not its severity in the abstract. Edible
 * reads as a good thing about the plant; stressed is the one that stops the app
 * asking for anything; toxic is a caution rather than an alarm.
 */
export type Tone = "alert" | "good" | "warn" | "neutral";

export interface Flag {
  label: string;
  tone: Tone;
}

export function flagsFor(
  specimen: Specimen | undefined,
  t: Strings,
  /**
   * Specimens the guide cannot schedule where they are. Such a plant produces
   * no Task at all, so without a flag it is simply absent — and absent is
   * indistinguishable from fine.
   */
  unschedulable?: Set<string>,
): Flag[] {
  if (!specimen) return [];
  const flags: Flag[] = [];

  if (unschedulable?.has(specimen.id))
    flags.push({ label: t.notSchedulable, tone: "alert" });

  if (specimen.condition !== "healthy")
    flags.push({
      label: specimen.condition === "stressed" ? t.stressed : t.recovering,
      tone: "alert",
    });
  if (specimen.species.is_edible) flags.push({ label: t.edible, tone: "good" });
  if (specimen.species.is_toxic) flags.push({ label: t.toxic, tone: "warn" });
  if (specimen.soil_quality === "compacted")
    flags.push({ label: t.compactedSoil, tone: "neutral" });
  if (!specimen.last_watered_known)
    flags.push({ label: t.wateringUnknown, tone: "neutral" });

  return flags;
}
